import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { OverviewTile } from '../defaults'
import { FormGroup } from '@/components/ui/form'
import { tileTypeToKey } from './TileWrapper'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useTranslation } from 'react-i18next'
import { FormValues, MAIN_FIELD, SIDEBAR_FIELD } from '../form'
import { useFieldArray, useFormContext } from 'react-hook-form'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import OverviewTileType from 'app/frontend/types/custom/OverviewTileType'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Label } from '@/components/ui/label'
import { Info } from 'lucide-react'
import { ConfigField, configFieldsByTileType } from '../config'
import TileSelect from './TileSelect'
import TileTypeOptions from './TileTypeOptions'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SIDEBAR_TILE_SLOTS } from './tiles/SidebarTilesTile'

interface Props {
  index: number
  initialTileState: OverviewTile
  handleClose: () => void
}

type ConfigFieldName =
  | `${typeof MAIN_FIELD | typeof SIDEBAR_FIELD}.${number}.config.${string}`

const TileConfigDialog = ({ index, initialTileState, handleClose }: Props) => {
  const { t } = useTranslation()
  const { watch, setValue, getValues, setError, clearErrors } =
    useFormContext<FormValues>()

  const section: typeof MAIN_FIELD | typeof SIDEBAR_FIELD =
    initialTileState.position === 'main' ? MAIN_FIELD : SIDEBAR_FIELD
  const { remove, update } = useFieldArray<FormValues>({ name: section })
  const tile: OverviewTile = watch(section)[index]

  const alertText = t(
    `overview.tiles.${tileTypeToKey(initialTileState.type)}.alert`,
    {
      defaultValue: '',
    },
  )

  const handleCancel = () => {
    if (tile.pending) {
      remove(index)
    } else {
      update(index, initialTileState)
    }

    handleClose()
  }

  const handleContinue = () => {
    let hasErrors = false

    configFieldsByTileType[tile.type].flat().forEach((field) => {
      if (validateField(field)) {
        hasErrors = true
      }
    })

    if (hasErrors) return

    setValue(`${section}.${index}.pending`, false)
    handleClose()
  }

  const validateField = (field: ConfigField) => {
    if (!field.validation) return false

    const fieldName =
      `${section}.${index}.config.${field.name}` as ConfigFieldName

    const value = getValues(fieldName)

    if (value == null) {
      clearErrors(fieldName)
      return false
    }

    const result = field.validation.safeParse(value)

    if (!result.success) {
      const message = result.error.issues[0]?.message ?? 'Invalid value'
      setError(fieldName, { type: 'validate', message })
      return true
    }

    clearErrors(fieldName)
    return false
  }

  return (
    <Dialog open onOpenChange={handleCancel}>
      <DialogContent
        className='max-w-xl'
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Configure {tile.position} tile</DialogTitle>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <FormGroup>
            <TileSelect
              name={`${section}.${index}.type`}
              configName={`${section}.${index}.config`}
              position={tile.position}
              customLabel='Type'
            />

            <SelectField
              name={`${section}.${index}.accessLevel`}
              layout='stacked'
              customLabel={
                <Label className='flex items-center justify-between gap-2'>
                  <span>Required access</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info size={14} />
                    </TooltipTrigger>
                    <TooltipContent
                      side='left'
                      className='max-w-xs font-normal'
                    >
                      Regardless of this setting, base member access will always
                      be required to view the team overview. You can use this to
                      display section-specific tiles to users without access to
                      the respective section (or hide them).
                    </TooltipContent>
                  </Tooltip>
                </Label>
              }
              className='w-50'
              options={[
                { label: 'Any', value: 'none' },
                { label: 'Member', value: 'member' },
                { label: 'Admin', value: 'admin' },
              ]}
              transformOptions={false}
              inModal
            />
          </FormGroup>

          <TileTypeOptions
            namePrefix={`${section}.${index}.config`}
            position={tile.position}
            tileType={tile.type}
          />

          {tile.type === 'Tile::SidebarTiles' ? (
            <Tabs defaultValue={SIDEBAR_TILE_SLOTS[0]}>
              <TabsList className='w-full'>
                {SIDEBAR_TILE_SLOTS.map((slot) => (
                  <TabsTrigger
                    key={slot}
                    value={slot}
                    className='grow'
                    disabled={!tile.config?.[slot]}
                  >
                    {t(`overview.tiles.sidebar-tiles.config.${slot}.label`)}{' '}
                    options
                  </TabsTrigger>
                ))}
              </TabsList>
              {SIDEBAR_TILE_SLOTS.map((slot) => (
                <TabsContent key={slot} value={slot} className='space-y-4'>
                  {tile.config?.[slot] ? (
                    <TileTypeOptions
                      namePrefix={`${section}.${index}.config.${slot}_config`}
                      position={'sidebar'}
                      tileType={tile.config?.[slot] as OverviewTileType}
                    />
                  ) : null}
                </TabsContent>
              ))}
            </Tabs>
          ) : null}
        </div>

        {alertText ? (
          <Alert className='mb-4'>
            <AlertDescription>{alertText}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant='outline' type='button' onClick={handleCancel}>
            Cancel
          </Button>
          <Button type='button' onClick={handleContinue}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TileConfigDialog
